import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { StatusIcon } from '../../icons/StatusIcons';
import { TabStyles } from '../../styles/TabStyles';
import { makeTestId } from '../../utils/Declarative';
import { ElemAttrs } from '../../utils/HtmlAttrs';
import { AlertBaseColorStyleName, AlertType as InlineAlertType } from '../AlertStyles';

export { InlineAlertType };

const AlertTestId: string = 'InlineAlert';
const IconClass: string = 'statusIcon';
export const ContentClass: string = 'contentContainer';
export const MessageClass: string = 'alertMessage';

const AlertBaseStyleName: string = TypeStyle.style({
  borderRadius: 2,
  borderLeft: `4px solid transparent`,
  boxSizing: 'border-box',
  color: TabStyles.Typography.Color.Primary,
  display: 'flex',
  fontSize: '12px',
  letterSpacing: '0.2px',
  lineHeight: 1.5,
  $nest: {
    [`& .${IconClass}`]: {
      flexShrink: 0,
      marginLeft: 8,
      marginTop: 5,
    },
    [`& .${ContentClass}`]: {
      display: 'flex',
      flexGrow: 1,
    },
    '& button': {
      margin: 0, // reset safari 2px side margin on button elements
    },
  },
});

export interface InlineAlertBaseProps {
  interactiveElement?: React.ReactNode;
  /**
   * Test id to be attached to the root element of the InlineAlertWidget.
   * The suffix '-InlineAlert' will be automatically added
   */
  testId: string;
  /**
   * The `type` of InlineAlert you choose should correspond to the type of message it conveys.
   * It will also affect the style of the InlineAlert
   */
  type: InlineAlertType;
}

export const InlineAlert = React.forwardRef<HTMLDivElement, InlineAlertBaseProps & ElemAttrs>((props, ref) => {
  const { children, className, interactiveElement, testId, type, ...htmlProps } = props;

  return (
    <div
      aria-atomic={true}
      aria-live='polite'
      {...htmlProps}
      className={TypeStyle.classes(AlertBaseColorStyleName, AlertBaseStyleName, className, type)}
      data-tb-test-id={makeTestId(testId, AlertTestId)}
      ref={ref}
    >
      <StatusIcon type={type} className={IconClass} />
      <div key='contentContainer' className={ContentClass}>
        <div className={MessageClass}>{children}</div>
        {interactiveElement}
      </div>
    </div>
  );
});
